
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatChartData, getLatestTelemetry, type TelemetryData } from './telemetry/utils/telemetryDataUtils';
import TelemetryHeader from './telemetry/dashboard/TelemetryHeader';
import TelemetryLoadingState from './telemetry/dashboard/TelemetryLoadingState';
import NoTelemetryData from './telemetry/dashboard/NoTelemetryData';
import TelemetryTabs from './telemetry/dashboard/TelemetryTabs';
import TelemetryHistoryTable from './telemetry/TelemetryHistoryTable';
import { Device } from './device-health/gis-map/types';

interface TelemetryDashboardProps {
  telemetryData: TelemetryData[];
  isLoading: boolean;
  telemetryType?: 'energy' | 'gas' | 'water';
  selectedSection?: string | null;
  onCardClick?: (section: string) => void;
  deviceId?: string;
  deviceData?: Device | null;
}

const TelemetryDashboard: React.FC<TelemetryDashboardProps> = ({ 
  telemetryData,
  isLoading,
  telemetryType = 'energy',
  selectedSection = null,
  onCardClick = () => {},
  deviceId,
  deviceData
}) => {
  const [activeTab, setActiveTab] = useState('all');
  
  // Refs for scrolling to sections
  const gatewayStatusRef = useRef<HTMLDivElement>(null);
  const performanceMetricsRef = useRef<HTMLDivElement>(null);
  const telemetryHistoryRef = useRef<HTMLDivElement>(null);
  
  // Scroll to selected section when it changes
  useEffect(() => {
    if (selectedSection === 'gatewayStatus' && gatewayStatusRef.current) {
      gatewayStatusRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (selectedSection === 'performanceMetrics' && performanceMetricsRef.current) {
      performanceMetricsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (selectedSection === 'telemetryHistory' && telemetryHistoryRef.current) {
      telemetryHistoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSection]);
  
  // Format and get data
  const chartData = formatChartData(telemetryData);
  const latestData = getLatestTelemetry(telemetryData);
  
  // Handle card click for navigation
  const handleCardClick = (section: string) => {
    onCardClick(section);
    
    // Show toast notification
    const sectionNames: Record<string, string> = {
      'online': 'Online Devices',
      'warning': 'Devices with Warnings',
      'critical': 'Critical Devices',
      'offline': 'Offline Devices',
      'readings': 'Device Readings'
    };
    
    toast(`Viewing ${sectionNames[section] || section} details`, {
      description: `Scrolling to the relevant section for more information.`,
      duration: 3000,
    });
  };

  if (isLoading) {
    return <TelemetryLoadingState />;
  }

  if (!latestData) {
    return <NoTelemetryData />;
  }

  // Get the title based on device data if available
  const getTitle = () => {
    if (deviceData && deviceId) {
      return `Telemetry Data for ${deviceData.name} (${deviceId})`;
    }
    return deviceId ? `Telemetry Data for ${deviceId}` : 'Telemetry Dashboard';
  };

  return (
    <Card>
      <TelemetryHeader lastUpdated={latestData.timestamp} title={getTitle()} />
      <CardContent>
        <TelemetryTabs 
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          chartData={chartData}
          latestData={latestData}
          selectedSection={selectedSection}
          onCardClick={handleCardClick}
          telemetryType={telemetryType}
          gatewayStatusRef={gatewayStatusRef}
          performanceMetricsRef={performanceMetricsRef}
          deviceId={deviceId}
          deviceData={deviceData}
        />
        
        {/* Telemetry History Table */}
        <div className="mt-8" ref={telemetryHistoryRef}>
          <TelemetryHistoryTable 
            telemetryData={telemetryData} 
            selectedSection={selectedSection} 
            onCardClick={onCardClick} 
            sectionName="telemetryHistory" 
            telemetryType={telemetryType}
            deviceId={deviceId}
            deviceData={deviceData}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default TelemetryDashboard;
